import { useMemo } from "react";
import { removeCommasAndSpaces } from "@/lib/formatting-utils.js";
import { unitSetupGaugeBarPadding } from "@/lib/enalysis-trends-gauge-utils.js";
import {
  isEnalysisClearanceZeroToMaxParam,
  isEnalysisDischargeOrSuctionPressureUnitSetupParam,
  isEnalysisLowToHighBandUnitSetupParam,
  isEnalysisMinDegreesReversalBandParam,
  isEnalysisMinNetRodLoadRatioBandParam,
  isEnalysisRodLoadPercentBandParam,
  isEnalysisVolumeEfficiencyBandParam,
  isEnalysisZeroToHundredBandParam,
} from "@/constants/enalysis-trends-constants.js";

// Gauge color constants
const COLORS = {
  red: "#dc2626",
  yellow: "#f59e0b",
  green: "#10b981",
  gray: "#6b7280",
  background: "#e5e7eb",
  white: "#ffffff",
};

// Gauge dimensions
const GAUGE_HEIGHT = 20;
const BAR_HEIGHT = 8;
const INDICATOR_RADIUS = 5;
const OPACITY = 0.8;

/**
 * Linear scale for 0–100 band (utilization %, compressor/mechanical efficiency): green between 0 and 100; red outside.
 * Scale extends past [0, 100] so red regions have visible width on the bar.
 */
const UTIL_SCALE_MIN = -20;
const UTIL_SCALE_MAX = 120;

/** Min degrees reversal: bar maps 0–200° so red past 180° has width. */
const MIN_DEG_SCALE_MIN = 0;
const MIN_DEG_SCALE_MAX = 200;

/** Min net rod load ratio: bar maps 0–120 so red past 100 has width. */
const MIN_NET_ROD_RATIO_SCALE_MIN = 0;
const MIN_NET_ROD_RATIO_SCALE_MAX = 120;

function clampPct(p) {
  return Math.max(0, Math.min(100, p));
}

/**
 * Mini Horizontal Gauge Component
 * Displays a compact horizontal gauge for table cells
 * @param {Object} param - Parameter data with currentValue, alarmSetPoints, etc.
 */
export default function MiniHorizontalGauge({ param }) {
  const gaugeData = useMemo(() => {
    let currentValueStr = removeCommasAndSpaces(param.currentValue);
    const currentValue = parseFloat(currentValueStr);
    const isValidCurrentValue =
      !Number.isNaN(currentValue) && Number.isFinite(currentValue);

    if (isEnalysisZeroToHundredBandParam(param)) {
      const range = UTIL_SCALE_MAX - UTIL_SCALE_MIN || 1;
      const greenStart = clampPct(((0 - UTIL_SCALE_MIN) / range) * 100);
      const greenEnd = clampPct(((100 - UTIL_SCALE_MIN) / range) * 100);
      let currentPos = 0;
      if (isValidCurrentValue && range > 0) {
        currentPos = clampPct(
          ((currentValue - UTIL_SCALE_MIN) / range) * 100
        );
      }
      return {
        layout: "zeroToHundredBand",
        greenStart,
        greenEnd,
        currentPos,
        isValidCurrentValue,
        currentValue,
      };
    }

    if (isEnalysisVolumeEfficiencyBandParam(param)) {
      const range = UTIL_SCALE_MAX - UTIL_SCALE_MIN || 1;
      const yellowStart = clampPct(((12 - UTIL_SCALE_MIN) / range) * 100);
      const greenStart = clampPct(((20 - UTIL_SCALE_MIN) / range) * 100);
      const greenEnd = clampPct(((100 - UTIL_SCALE_MIN) / range) * 100);
      let currentPos = 0;
      if (isValidCurrentValue && range > 0) {
        currentPos = clampPct(
          ((currentValue - UTIL_SCALE_MIN) / range) * 100
        );
      }
      return {
        layout: "volumeEfficiencyBand",
        yellowStart,
        greenStart,
        greenEnd,
        currentPos,
        isValidCurrentValue,
        currentValue,
      };
    }

    if (isEnalysisRodLoadPercentBandParam(param)) {
      const range = UTIL_SCALE_MAX - UTIL_SCALE_MIN || 1;
      const ROD_GREEN_END = 95;
      const ROD_YELLOW_END = 99.9;
      const pos0 = clampPct(((0 - UTIL_SCALE_MIN) / range) * 100);
      const pos95 = clampPct(((ROD_GREEN_END - UTIL_SCALE_MIN) / range) * 100);
      const pos99_9 = clampPct(
        ((ROD_YELLOW_END - UTIL_SCALE_MIN) / range) * 100
      );
      let currentPos = 0;
      if (isValidCurrentValue && range > 0) {
        currentPos = clampPct(
          ((currentValue - UTIL_SCALE_MIN) / range) * 100
        );
      }
      return {
        layout: "rodLoadPercentBand",
        pos0,
        pos95,
        pos99_9,
        currentPos,
        isValidCurrentValue,
        currentValue,
      };
    }

    if (isEnalysisMinDegreesReversalBandParam(param)) {
      const range = MIN_DEG_SCALE_MAX - MIN_DEG_SCALE_MIN || 1;
      const pos60 = clampPct(((60 - MIN_DEG_SCALE_MIN) / range) * 100);
      const pos70 = clampPct(((70 - MIN_DEG_SCALE_MIN) / range) * 100);
      const pos180 = clampPct(((180 - MIN_DEG_SCALE_MIN) / range) * 100);
      let currentPos = 0;
      if (isValidCurrentValue && range > 0) {
        currentPos = clampPct(
          ((currentValue - MIN_DEG_SCALE_MIN) / range) * 100
        );
      }
      return {
        layout: "minDegreesReversalBand",
        pos60,
        pos70,
        pos180,
        currentPos,
        isValidCurrentValue,
        currentValue,
      };
    }

    if (isEnalysisMinNetRodLoadRatioBandParam(param)) {
      const range =
        MIN_NET_ROD_RATIO_SCALE_MAX - MIN_NET_ROD_RATIO_SCALE_MIN || 1;
      const pos35 = clampPct(((35 - MIN_NET_ROD_RATIO_SCALE_MIN) / range) * 100);
      const pos40 = clampPct(((40 - MIN_NET_ROD_RATIO_SCALE_MIN) / range) * 100);
      const pos100 = clampPct(
        ((100 - MIN_NET_ROD_RATIO_SCALE_MIN) / range) * 100
      );
      let currentPos = 0;
      if (isValidCurrentValue && range > 0) {
        currentPos = clampPct(
          ((currentValue - MIN_NET_ROD_RATIO_SCALE_MIN) / range) * 100
        );
      }
      return {
        layout: "minNetRodLoadRatioBand",
        pos35,
        pos40,
        pos100,
        currentPos,
        isValidCurrentValue,
        currentValue,
      };
    }

    if (isEnalysisClearanceZeroToMaxParam(param)) {
      const minV = parseFloat(param.apiData?.displayMin);
      const maxDomain = parseFloat(param.apiData?.displayMax);
      const maxGreen = parseFloat(param.apiData?.maxValue);
      const range =
        Number.isFinite(maxDomain) && Number.isFinite(minV)
          ? maxDomain - minV
          : 1;
      const pos0 = clampPct(((0 - minV) / range) * 100);
      const posMaxGreen = clampPct(((maxGreen - minV) / range) * 100);
      let currentPos = 0;
      if (isValidCurrentValue && range > 0) {
        currentPos = clampPct(((currentValue - minV) / range) * 100);
      }
      return {
        layout: "clearanceZeroToMax",
        pos0,
        posMaxGreen,
        currentPos,
        isValidCurrentValue,
        currentValue,
        clearanceMaxGreen: maxGreen,
      };
    }

    if (isEnalysisDischargeOrSuctionPressureUnitSetupParam(param)) {
      const scaleMax = parseFloat(param.apiData?.maxValue);
      const displayMin = parseFloat(param.apiData?.displayMin);
      const minV = Number.isFinite(displayMin) ? displayMin : 0;
      const maxV = Number.isFinite(scaleMax) && scaleMax > 0 ? scaleMax : 100;
      const range = maxV - minV || 1;
      const pos0 = clampPct(((0 - minV) / range) * 100);
      const pos90 = clampPct(((0.9 * maxV - minV) / range) * 100);
      const pos95 = clampPct(((0.95 * maxV - minV) / range) * 100);
      let currentPos = 0;
      if (isValidCurrentValue && range > 0) {
        currentPos = clampPct(((currentValue - minV) / range) * 100);
      }
      return {
        layout: param.apiData?.gaugeLayout ?? "dischargePressureUnitSetup",
        pos0,
        pos90,
        pos95,
        currentPos,
        isValidCurrentValue,
        currentValue,
        scaleMax: maxV,
      };
    }

    if (isEnalysisLowToHighBandUnitSetupParam(param)) {
      const limit = parseFloat(param.apiData?.maxValue);
      const scaleTop = Number.isFinite(limit) && limit > 0 ? limit : 100;
      const pad = unitSetupGaugeBarPadding(scaleTop);
      const displayMinParsed = parseFloat(param.apiData?.displayMin);
      const displayMaxParsed = parseFloat(param.apiData?.displayMax);
      const minV = Number.isFinite(displayMinParsed) ? displayMinParsed : -pad;
      const maxV = Number.isFinite(displayMaxParsed) ? displayMaxParsed : scaleTop + pad;
      const range = maxV - minV || 1;
      const hi = parseFloat(param.alarmSetPoints?.hi);
      const hihi = parseFloat(param.alarmSetPoints?.hihi);
      const hiForPos = Number.isFinite(hi) ? Math.min(hi, scaleTop) : 0;
      const hihiForPos = Number.isFinite(hihi) ? Math.min(hihi, scaleTop) : scaleTop;
      const pos0 = clampPct(((0 - minV) / range) * 100);
      const posHi = clampPct(((hiForPos - minV) / range) * 100);
      const posHihi = clampPct(((hihiForPos - minV) / range) * 100);
      let currentPos = 0;
      if (isValidCurrentValue && range > 0) {
        currentPos = clampPct(((currentValue - minV) / range) * 100);
      }
      return {
        layout: param.apiData?.gaugeLayout ?? "dischargeTemperatureUnitSetup",
        pos0,
        posHi,
        posHihi,
        currentPos,
        isValidCurrentValue,
        currentValue,
        scaleMax: scaleTop,
      };
    }

    const alarmSetPoints = param.alarmSetPoints || {};
    const lolo = parseFloat(alarmSetPoints.lolo) || 0;
    const lo = parseFloat(alarmSetPoints.lo) || 0;
    const hi = parseFloat(alarmSetPoints.hi) || 0;
    const hihi = parseFloat(alarmSetPoints.hihi) || 0;

    const minValue = parseFloat(param.apiData?.minValue) || 0;
    const maxValue = parseFloat(param.apiData?.maxValue) || 100;
    const range = maxValue - minValue || 1;

    const sortedAlarms = [lolo, lo, hi, hihi].sort((a, b) => a - b);
    const sortedLolo = sortedAlarms[0];
    const sortedLo = sortedAlarms[1];
    const sortedHi = sortedAlarms[2];
    const sortedHihi = sortedAlarms[3];

    const loloPos = clampPct(((sortedLolo - minValue) / range) * 100);
    const loPos = clampPct(((sortedLo - minValue) / range) * 100);
    const hiPos = clampPct(((sortedHi - minValue) / range) * 100);
    const hihiPos = clampPct(((sortedHihi - minValue) / range) * 100);

    let currentPos = 0;
    if (isValidCurrentValue && range > 0) {
      currentPos = clampPct(((currentValue - minValue) / range) * 100);
    }

    return {
      layout: "alarms",
      loloPos,
      loPos,
      hiPos,
      hihiPos,
      currentPos,
      isValidCurrentValue,
      currentValue,
    };
  }, [param]);

  const barY = (GAUGE_HEIGHT - BAR_HEIGHT) / 2;

  const currentValueColor = useMemo(() => {
    if (!gaugeData.isValidCurrentValue) return COLORS.gray;
    if (gaugeData.layout === "zeroToHundredBand") {
      const v = gaugeData.currentValue;
      return v >= 0 && v <= 100 ? "#1f2937" : COLORS.red;
    }
    if (gaugeData.layout === "volumeEfficiencyBand") {
      const v = gaugeData.currentValue;
      if (v < 12 || v > 100) return COLORS.red;
      if (v < 20) return COLORS.yellow;
      return "#1f2937";
    }
    if (gaugeData.layout === "rodLoadPercentBand") {
      const v = gaugeData.currentValue;
      if (v < 0 || v >= 99.9) return COLORS.red;
      if (v > 95) return COLORS.yellow;
      return "#1f2937";
    }
    if (gaugeData.layout === "minDegreesReversalBand") {
      const v = gaugeData.currentValue;
      if (v < 0 || v > 180 || v <= 60) return COLORS.red;
      if (v < 70) return COLORS.yellow;
      return "#1f2937";
    }
    if (gaugeData.layout === "minNetRodLoadRatioBand") {
      const v = gaugeData.currentValue;
      if (v < 0 || v > 100 || v <= 35) return COLORS.red;
      if (v < 40) return COLORS.yellow;
      return "#1f2937";
    }
    if (gaugeData.layout === "clearanceZeroToMax") {
      const v = gaugeData.currentValue;
      const cap = gaugeData.clearanceMaxGreen;
      if (!Number.isFinite(cap) || cap <= 0) return COLORS.gray;
      if (v < 0 || v > cap) return COLORS.red;
      return "#1f2937";
    }
    if (
      gaugeData.layout === "dischargePressureUnitSetup"
      || gaugeData.layout === "suctionPressureUnitSetup"
    ) {
      const v = gaugeData.currentValue;
      const sm = gaugeData.scaleMax;
      if (!Number.isFinite(sm) || sm <= 0) return COLORS.gray;
      if (v < 0) return COLORS.red;
      if (v >= 0.95 * sm) return COLORS.red;
      if (v >= 0.9 * sm) return COLORS.yellow;
      return "#1f2937";
    }
    if (
      gaugeData.layout === "dischargeTemperatureUnitSetup"
      || gaugeData.layout === "blowbyUnitSetup"
    ) {
      const v = gaugeData.currentValue;
      const sm = gaugeData.scaleMax;
      const hi = parseFloat(param.alarmSetPoints?.hi);
      const hihi = parseFloat(param.alarmSetPoints?.hihi);
      if (!Number.isFinite(sm) || sm <= 0) return COLORS.gray;
      if (v < 0) return COLORS.red;
      if (Number.isFinite(hihi) && v >= hihi) return COLORS.red;
      if (Number.isFinite(hi) && v >= hi && v < hihi) return COLORS.yellow;
      return "#1f2937";
    }
    const { currentValue } = gaugeData;
    const { hihi } = param.alarmSetPoints || {};
    return currentValue > hihi ? COLORS.red : "#1f2937";
  }, [
    gaugeData,
    param.alarmSetPoints,
  ]);

  const renderZone = (startPos, endPos, color) => {
    if (endPos <= startPos) return null;
    return (
      <rect
        key={`${startPos}-${endPos}`}
        x={`${startPos}%`}
        y={barY}
        width={`${endPos - startPos}%`}
        height={BAR_HEIGHT}
        fill={color}
        opacity={OPACITY}
      />
    );
  };

  const zones =
    gaugeData.layout === "zeroToHundredBand" ? (
      <>
        {renderZone(0, gaugeData.greenStart, COLORS.red)}
        {renderZone(gaugeData.greenStart, gaugeData.greenEnd, COLORS.green)}
        {renderZone(gaugeData.greenEnd, 100, COLORS.red)}
      </>
    ) : gaugeData.layout === "volumeEfficiencyBand" ? (
      <>
        {renderZone(0, gaugeData.yellowStart, COLORS.red)}
        {renderZone(gaugeData.yellowStart, gaugeData.greenStart, COLORS.yellow)}
        {renderZone(gaugeData.greenStart, gaugeData.greenEnd, COLORS.green)}
        {renderZone(gaugeData.greenEnd, 100, COLORS.red)}
      </>
    ) : gaugeData.layout === "rodLoadPercentBand" ? (
      <>
        {renderZone(0, gaugeData.pos0, COLORS.red)}
        {renderZone(gaugeData.pos0, gaugeData.pos95, COLORS.green)}
        {renderZone(gaugeData.pos95, gaugeData.pos99_9, COLORS.yellow)}
        {renderZone(gaugeData.pos99_9, 100, COLORS.red)}
      </>
    ) : gaugeData.layout === "minDegreesReversalBand" ? (
      <>
        {renderZone(0, gaugeData.pos60, COLORS.red)}
        {renderZone(gaugeData.pos60, gaugeData.pos70, COLORS.yellow)}
        {renderZone(gaugeData.pos70, gaugeData.pos180, COLORS.green)}
        {renderZone(gaugeData.pos180, 100, COLORS.red)}
      </>
    ) : gaugeData.layout === "minNetRodLoadRatioBand" ? (
      <>
        {renderZone(0, gaugeData.pos35, COLORS.red)}
        {renderZone(gaugeData.pos35, gaugeData.pos40, COLORS.yellow)}
        {renderZone(gaugeData.pos40, gaugeData.pos100, COLORS.green)}
        {renderZone(gaugeData.pos100, 100, COLORS.red)}
      </>
    ) : gaugeData.layout === "clearanceZeroToMax" ? (
      <>
        {renderZone(0, gaugeData.pos0, COLORS.red)}
        {renderZone(gaugeData.pos0, gaugeData.posMaxGreen, COLORS.green)}
        {renderZone(gaugeData.posMaxGreen, 100, COLORS.red)}
      </>
    ) : gaugeData.layout === "dischargePressureUnitSetup"
    || gaugeData.layout === "suctionPressureUnitSetup" ? (
      <>
        {renderZone(0, gaugeData.pos0, COLORS.red)}
        {renderZone(gaugeData.pos0, gaugeData.pos90, COLORS.green)}
        {renderZone(gaugeData.pos90, gaugeData.pos95, COLORS.yellow)}
        {renderZone(gaugeData.pos95, 100, COLORS.red)}
      </>
    ) : gaugeData.layout === "dischargeTemperatureUnitSetup"
    || gaugeData.layout === "blowbyUnitSetup" ? (
      <>
        {renderZone(0, gaugeData.pos0, COLORS.red)}
        {renderZone(gaugeData.pos0, gaugeData.posHi, COLORS.green)}
        {renderZone(gaugeData.posHi, gaugeData.posHihi, COLORS.yellow)}
        {renderZone(gaugeData.posHihi, 100, COLORS.red)}
      </>
    ) : (
      <>
        {renderZone(0, gaugeData.loloPos, COLORS.red)}
        {renderZone(gaugeData.loloPos, gaugeData.loPos, COLORS.yellow)}
        {renderZone(gaugeData.loPos, gaugeData.hiPos, COLORS.green)}
        {renderZone(gaugeData.hiPos, gaugeData.hihiPos, COLORS.yellow)}
        {renderZone(gaugeData.hihiPos, 100, COLORS.red)}
      </>
    );

  return (
    <div
      className='flex flex-col items-center w-full relative'
      style={{ height: GAUGE_HEIGHT }}>
      <svg
        width='100%'
        height={GAUGE_HEIGHT}
        viewBox={`0 0 100 ${GAUGE_HEIGHT}`}
        preserveAspectRatio='none'
        className='overflow-visible'
        style={{ position: "absolute", top: 0, left: 0 }}>
        <rect
          x='0%'
          y={barY}
          width='100%'
          height={BAR_HEIGHT}
          fill={COLORS.background}
        />
        {zones}
      </svg>

      {gaugeData.isValidCurrentValue && (
        <div
          style={{
            position: "absolute",
            left: `${gaugeData.currentPos}%`,
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: INDICATOR_RADIUS * 2,
            height: INDICATOR_RADIUS * 2,
            borderRadius: "50%",
            backgroundColor: currentValueColor,
            border: `2px solid ${COLORS.white}`,
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
}
